import type { EmailTemplateDetail, EmailTemplateStatus } from '../types/emailTemplate'
import type { EmailTemplateFormState, TemplateFormValidationContext } from '../types'
import { META_DATA_WORKFLOW_TYPES } from './constants'

export const emptyEmailTemplateForm: EmailTemplateFormState = {
  template_status: 'ACTIVE',
  passthru_mailgun: '',
  template_name: '',
  template_description: '',
  fk_workflow_id: '',
  template_metadata: '',
  email_from: '',
  email_to: '',
  email_cc: '',
  email_bcc: '',
  reply_to: '',
  email_subject: '',
  email_body: '',
  ach_instructions: '',
  has_attachment: '0',
  underwriter_require_yes: '',
  underwriter_require_no: '',
  underwriter_require_yes_sms: '',
  underwriter_require_no_sms: '',
  certificate_partial_payment: '',
  certificate_complete_payment: '',
  contract_invoice: '',
  policy_activation_invoice: '',
  paid_reminder_first: '',
  paid_reminder_other: '',
  application_refund: '',
  certificate_refund: '',
  rent_partial_refund: '',
  generate_certificate_lease_pass: '',
  policy_updated: '',
  account_creation: '',
  account_reactivation: '',
  email_body_table: '',
  notification_title: '',
  notification_body: '',
  sms_body: '',
}

function s(v: unknown): string {
  if (v === null || v === undefined) return ''
  return String(v)
}

/** Treat CKEditor empty states (`<p><br></p>`, `&nbsp;`) as blank for required validation. */
export function isRichTextEffectivelyEmpty(html: string): boolean {
  const stripped = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim()
  return stripped.length === 0
}

/**
 * Map API record into form. `detail` is full GET response; optional `workflowIdOverride`
 * when patching after workflow change.
 */
export function mapDetailToForm(detail: EmailTemplateDetail): EmailTemplateFormState {
  return {
    ...emptyEmailTemplateForm,
    template_status: (detail.template_status as EmailTemplateStatus) || 'ACTIVE',
    passthru_mailgun: s(detail.passthru_mailgun) || '0',
    template_name: s(detail.template_name),
    template_description: s(detail.template_description),
    fk_workflow_id: s(detail.fk_workflow_id),
    template_metadata: s(detail.template_metadata),
    email_from: s(detail.email_from),
    email_to: s(detail.email_to),
    email_cc: s(detail.email_cc),
    email_bcc: s(detail.email_bcc),
    reply_to: s(detail.reply_to),
    email_subject: s(detail.email_subject),
    email_body: s(detail.email_body),
    ach_instructions: s(detail.ach_instructions),
    has_attachment: s(detail.has_attachment) || '0',
    underwriter_require_yes: s(detail.underwriter_require_yes),
    underwriter_require_no: s(detail.underwriter_require_no),
    underwriter_require_yes_sms: s(detail.underwriter_require_yes_sms),
    underwriter_require_no_sms: s(detail.underwriter_require_no_sms),
    certificate_partial_payment: s(detail.certificate_partial_payment),
    certificate_complete_payment: s(detail.certificate_complete_payment),
    contract_invoice: s(detail.contract_invoice),
    policy_activation_invoice: s(detail.policy_activation_invoice),
    paid_reminder_first: s(detail.paid_reminder_first),
    paid_reminder_other: s(detail.paid_reminder_other),
    application_refund: s(detail.application_refund),
    certificate_refund: s(detail.certificate_refund),
    rent_partial_refund: s(detail.rent_partial_refund),
    generate_certificate_lease_pass: s(detail.generate_certificate_lease_pass),
    policy_updated: s(detail.policy_updated),
    account_creation: s(detail.account_creation),
    account_reactivation: s(detail.account_reactivation),
    email_body_table: s(detail.email_body_table),
    notification_title: s(detail.notification_title),
    notification_body: s(detail.notification_body),
    sms_body: s(detail.sms_body),
  }
}

/**
 * Build POST body matching Angular `saveTemplate()`:
 * Base fields are always sent. Workflow-block fields are included only if applicable to the workflow.
 * Omits `checkbox_data` (handled server-side; Angular removed it on save).
 */
export function formToApiPayload(
  values: EmailTemplateFormState,
  workflowType?: string,
  templateDetail?: EmailTemplateDetail | null,
): Record<string, unknown> {
  const wId = values.fk_workflow_id
    ? Number.parseInt(String(values.fk_workflow_id), 10)
    : NaN
  const passthruOut = values.passthru_mailgun || ''

  // Base fields matching Angular templateForm controls
  const payload: Record<string, unknown> = {
    fk_workflow_id: Number.isFinite(wId) && wId > 0 ? wId : values.fk_workflow_id,
    template_status: values.template_status,
    email_subject: values.email_subject,
    email_body: values.email_body,
    email_from: values.email_from || '',
    ach_instructions: values.ach_instructions || '',
    has_attachment: values.has_attachment || '',
    email_to: values.email_to,
    reply_to: values.reply_to || '',
    email_cc: values.email_cc || '',
    email_bcc: values.email_bcc || '',
    template_description: values.template_description || '',
    template_name: values.template_name,
    passthru_mailgun: passthruOut,
  }

  // Include conditional fields only if matching workflow / flags in Angular
  const wt = workflowType || templateDetail?.workflow_type
  if (wt) {
    if (wt === 'CO-APPLICANT_INVITE') {
      payload.underwriter_require_yes = values.underwriter_require_yes || ''
      payload.underwriter_require_no = values.underwriter_require_no || ''
      payload.underwriter_require_yes_sms = values.underwriter_require_yes_sms || ''
      payload.underwriter_require_no_sms = values.underwriter_require_no_sms || ''
    }
    if (['PAYMENT_RECEIPT', 'PAYMENT_RECEIPT_ACCESS_FEE'].includes(wt)) {
      payload.certificate_partial_payment = values.certificate_partial_payment || ''
      payload.certificate_complete_payment = values.certificate_complete_payment || ''
    }
    if (['INVOICE_REMINDER', 'INVOICE_REMINDER_ACCESS_FEE'].includes(wt)) {
      payload.contract_invoice = values.contract_invoice || ''
      payload.policy_activation_invoice = values.policy_activation_invoice || ''
    }
    if (wt === 'RESIDENT_PAID_REMINDER') {
      payload.paid_reminder_first = values.paid_reminder_first || ''
      payload.paid_reminder_other = values.paid_reminder_other || ''
    }
    if (['REFUND_PROCESSED', 'REFUND_PROCESSED_ACCESS_FEE'].includes(wt)) {
      payload.certificate_refund = values.certificate_refund || ''
      payload.application_refund = values.application_refund || ''
      payload.rent_partial_refund = values.rent_partial_refund || ''
    }
    if (['GENERATE_CERTIFICATE', 'GENERATE_CERTIFICATE_ACCESS_FEE'].includes(wt)) {
      payload.generate_certificate_lease_pass = values.generate_certificate_lease_pass || ''
      payload.policy_updated = values.policy_updated || ''
    }
    if (wt === 'PROPERTY_ADMIN_CREATION_LIBERTY') {
      payload.account_creation = values.account_creation || ''
      payload.account_reactivation = values.account_reactivation || ''
    }
    if (['ACTIVE_DEFAULTS_RESIDENTS_COMPANY', 'ACTIVE_DEFAULTS_RESIDENTS'].includes(wt)) {
      payload.email_body_table = values.email_body_table || ''
    }
    if (needsTemplateMetadata(wt)) {
      payload.template_metadata = values.template_metadata || ''
    }
  }

  if (templateDetail?.is_sms === '1') {
    payload.sms_body = values.sms_body || ''
  }
  if (templateDetail?.is_notification === '1') {
    payload.notification_title = values.notification_title || ''
    payload.notification_body = values.notification_body || ''
  }

  return payload
}

export function needsTemplateMetadata(wt: string | undefined): boolean {
  if (!wt) return false
  return (META_DATA_WORKFLOW_TYPES as readonly string[]).includes(wt)
}

/** Single-field message for on-blur validation (reuses `validateTemplateForm`). */
export function getTemplateFieldError(
  field: keyof EmailTemplateFormState,
  values: EmailTemplateFormState,
  context: TemplateFormValidationContext,
): string | null {
  const { fieldErrors } = validateTemplateForm(values, context)
  return fieldErrors[field] ?? null
}

/**
 * Return first validation error (Angular `checkValidation` style single banner) and field-level keys for inline messages.
 */
export function validateTemplateForm(
  v: EmailTemplateFormState,
  context: TemplateFormValidationContext,
): { summary: string | null; fieldErrors: Partial<Record<keyof EmailTemplateFormState, string>> } {
  const fieldErrors: Partial<Record<keyof EmailTemplateFormState, string>> = {}

  const set = (k: keyof EmailTemplateFormState, msg: string) => {
    fieldErrors[k] = msg
  }

  if (!v.template_name?.trim()) set('template_name', 'Template name is required.')
  if (!v.fk_workflow_id) set('fk_workflow_id', 'Please select a workflow.')
  if (v.template_status !== 'ACTIVE' && v.template_status !== 'DISABLED') {
    set('template_status', 'Template status is required.')
  }
  if (!v.email_to?.trim()) set('email_to', 'To is required.')
  if (!v.email_subject?.trim()) set('email_subject', 'Email subject is required.')
  if (!v.email_body?.trim() || isRichTextEffectivelyEmpty(v.email_body)) {
    set('email_body', 'Template body is required.')
  }

  if (context.workflowType && needsTemplateMetadata(context.workflowType)) {
    const n = Number.parseInt(String(v.template_metadata || ''), 10)
    if (!v.template_metadata?.toString().trim() || Number.isNaN(n) || n < 1) {
      set(
        'template_metadata',
        context.workflowType === 'RESIDENT_NOT_PAID'
          ? 'Cron application age (hours) must be greater than 0.'
          : 'Application age (hours) must be greater than 0.',
      )
    }
  }

  if (context.isNotification === '1') {
    if (!v.notification_title?.trim()) set('notification_title', 'Notification title is required.')
    if (!v.notification_body?.trim()) set('notification_body', 'Notification body is required.')
  }

  if (context.isSms === '1') {
    if (!v.sms_body?.trim()) set('sms_body', 'SMS body is required.')
  }

  const firstKey = Object.keys(fieldErrors)[0] as keyof EmailTemplateFormState | undefined
  const summary = firstKey ? (fieldErrors[firstKey] ?? 'Please fix validation errors before saving.') : null
  return { summary, fieldErrors }
}

export type { EmailTemplateFormState, TemplateFormValidationContext } from '../types'
